import {Injectable, Injector} from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpContextToken
} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AuthService} from '../services/auth/auth.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {StorageMap} from '@ngx-pwa/local-storage';
import {AlertService} from '../services/alert/alert.service';

// Contexte de requête permettant de sonder un endpoint optionnel (ex: assessment/state)
// sans afficher de toast d'erreur si l'API répond en erreur (404 attendu)
export const SKIP_ERROR_ALERT = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

  constructor(
    private injector: Injector,
    private alertSrv: AlertService,
    private authSrv: AuthService,
    private device: DeviceDetectorService
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const storageSrv = this.injector.get(StorageMap);
    // Ajout des headers de base d'abord
    let modifiedReq = req.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'ApiKey': environment.api.publicKey,
        'Device': this.device.isDesktop() ? 'desktop' : 'mobile'
      }
    });

    // Récupération de la plateforme et du token
    return storageSrv.get('platform').pipe(
      switchMap(platform => {
        // Ajout de la plateforme si disponible
        if (!modifiedReq.headers.get('Platform') && platform) {
          modifiedReq = modifiedReq.clone({
            setHeaders: {
              'Platform': `${platform}`
            }
          });
        }

        // Récupération du token
        return storageSrv.get('token');
      }),
      switchMap(async token => {
        // Vérification de l'expiration du token (async nécessaire ici)
        const tokenExpired = token ? this.authSrv.tokenExpired(token) : true;

        // Ajout du token si valide
        if (token && !tokenExpired) {
          modifiedReq = modifiedReq.clone({
            setHeaders: {
              'Authorization': `Bearer ${token}`
            }
          });
        }

        return modifiedReq;
      }),
      switchMap(finalReq => {
        return next.handle(finalReq).pipe(
          catchError((err: any) => {
            if (err instanceof HttpErrorResponse) {
              if (!environment.production) {
                if (err.message) {
                  console.error(`GS: HTTP Error: ${err.message}`);
                } else {
                  console.error(err);
                }
              }

              // Unauthorized - déconnexion automatique (sauf sur l'endpoint de login)
              if (err.status === 401) {
                if (!err.url?.includes('auth/signin')) {
                  this.authSrv.logout().then();
                }
                return throwError(() => err);
              }

              // Affichage d'une alerte d'erreur
              if (err.error?.message && !req.context.get(SKIP_ERROR_ALERT)) {
                this.alertSrv.error(err.error.message);
              }
            }

            return throwError(() => err);
          })
        );
      })
    );
  }

}
